// pages/GroupAdminPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Briefcase, MessageSquare, TrendingUp, Download, Plus, Search, Filter, MoreVertical, Send, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import BottomNav from '../components/common/BottomNav';
import JobPostingForm from '../components/groups/JobPostingForm';
import JobCompletionApprovals from '../components/admin/JobCompletionApprovals';
import Modal from '../components/common/Modal';
import { useAuth } from '../context/AuthContext';
import { useAdmin } from '../hooks/useAdmin';
import { useTrust } from '../context/TrustContext';
import { useNotification } from '../context/NotificationContext';
import { getDemoGroupsData } from '../utils/demoGroupsData';

const GroupAdminPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userTrust } = useTrust();
  const { addNotification } = useNotification();
  const {
    groupJobs,
    jobApplications,
    groupMembers,
    communications,
    analytics,
    postJob,
    deleteJob,
    sendMessage,
    exportData,
    canPerformAdminAction
  } = useAdmin();

  const [activeTab, setActiveTab] = useState('overview');
  const [group, setGroup] = useState(null);
  const [showJobPostingModal, setShowJobPostingModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [messageForm, setMessageForm] = useState({
    subject: '',
    content: '',
    recipients: 'all'
  });

  // Load group data
  useEffect(() => {
    const groups = getDemoGroupsData();
    const currentGroup = groups.find(g => g.id === id);
    setGroup(currentGroup);
  }, [id]);

  // Check if user is admin of this group
  const isGroupAdmin = group && (
    group.creatorId === user?.id ||
    group.creatorName === user?.name ||
    group.isUserCreated
  );

  if (!group) {
    return <div>Loading...</div>;
  }

  if (!isGroupAdmin) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="mx-auto text-red-500 mb-3" size={48} />
          <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">You don't have admin permissions for this group.</p>
          <button
            onClick={() => navigate(`/groups/${id}`)}
            className="mt-4 text-blue-600 hover:underline"
          >
            Return to group page
          </button>
        </div>
      </div>
    );
  }

  const handlePostJob = async (jobData) => {
    if (!canPerformAdminAction('postJob')) {
      addNotification({
        type: 'error',
        title: 'Insufficient Permissions',
        message: 'You need a trust score of 3.5+ to post jobs',
        showToast: true
      });
      return;
    }

    try {
      await postJob(id, jobData);
      addNotification({
        type: 'success',
        title: 'Job Posted!',
        message: 'Your job has been posted successfully',
        showToast: true
      });
      setShowJobPostingModal(false);
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message,
        showToast: true
      });
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        await deleteJob(id, jobId);
        addNotification({
          type: 'success',
          title: 'Job Deleted',
          message: 'The job has been removed',
          showToast: true
        });
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: error.message,
          showToast: true
        });
      }
    }
  };

  const handleSendMessage = async () => {
    if (!canPerformAdminAction('sendAnnouncements')) {
      addNotification({
        type: 'error',
        title: 'Insufficient Permissions',
        message: 'You need a trust score of 3.5+ to send announcements',
        showToast: true
      });
      return;
    }

    try {
      await sendMessage(id, messageForm);
      addNotification({
        type: 'success',
        title: 'Message Sent!',
        message: 'Your announcement has been sent to members',
        showToast: true
      });
      setShowMessageModal(false);
      setMessageForm({ subject: '', content: '', recipients: 'all' });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message,
        showToast: true
      });
    }
  };

  const handleExportData = (dataType) => {
    if (!canPerformAdminAction('exportData')) {
      addNotification({
        type: 'error',
        title: 'Insufficient Permissions',
        message: 'You need a trust score of 4.0+ to export data',
        showToast: true
      });
      return;
    }

    try {
      exportData(id, dataType);
      addNotification({
        type: 'success',
        title: 'Export Started',
        message: `${dataType} data is being downloaded`,
        showToast: true
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Export Failed',
        message: error.message,
        showToast: true
      });
    }
  };

  // Filter functions
  const filteredJobs = (groupJobs[id] || []).filter(job => {
    if (filterStatus !== 'all' && job.status !== filterStatus) return false;
    if (searchTerm && !job.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const filteredMembers = (groupMembers[id] || []).filter(member => {
    if (searchTerm && !member.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const groupAnalytics = analytics[id] || {
    totalMembers: 0,
    activeMembers: 0,
    totalUnits: 0,
    totalRevenue: 0,
    jobsPosted: 0,
    jobsCompleted: 0,
    averageTrust: 0,
    growthRate: 0,
    completionRate: 0,
    memberSatisfaction: 0,
    tokensDistributed: 0
  };

  return (
    <div className="container mx-auto px-4 pb-20">
      {/* Header */}
      <div className="mt-6 mb-6">
        <button
          onClick={() => navigate(`/groups/${id}`)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={20} className="mr-1" />
          Back to Group
        </button>
        
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold">{group.title} - Admin Dashboard</h1>
            <p className="text-gray-600 mt-1">Manage your group and coordinate logistics</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">Your Trust Score</div>
            <div className="text-2xl font-bold text-blue-600">{userTrust?.score || 3.0}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-6 overflow-x-auto">
        <button
          className={`px-4 py-2 whitespace-nowrap ${activeTab === 'overview' ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : 'text-gray-500'}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`px-4 py-2 whitespace-nowrap ${activeTab === 'jobs' ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : 'text-gray-500'}`}
          onClick={() => setActiveTab('jobs')}
        >
          Jobs & Applications
        </button>
        <button
          className={`px-4 py-2 whitespace-nowrap ${activeTab === 'members' ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : 'text-gray-500'}`}
          onClick={() => setActiveTab('members')}
        >
          Members
        </button>
        <button
          className={`px-4 py-2 whitespace-nowrap ${activeTab === 'communications' ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : 'text-gray-500'}`}
          onClick={() => setActiveTab('communications')}
        >
          Communications
        </button>
        <button
          className={`px-4 py-2 whitespace-nowrap ${activeTab === 'analytics' ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : 'text-gray-500'}`}
          onClick={() => setActiveTab('analytics')}
        >
          Analytics
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-blue-600" size={24} />
              <span className="text-xs text-gray-500">Total</span>
            </div>
            <div className="text-2xl font-bold">{groupAnalytics.totalMembers}</div>
            <div className="text-sm text-gray-600">Members</div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <Briefcase className="text-green-600" size={24} />
              <span className="text-xs text-gray-500">Active</span>
            </div>
            <div className="text-2xl font-bold">{groupAnalytics.jobsPosted}</div>
            <div className="text-sm text-gray-600">Jobs Posted</div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="text-yellow-600" size={24} />
              <span className="text-xs text-gray-500">Rate</span>
            </div>
            <div className="text-2xl font-bold">{groupAnalytics.completionRate}%</div>
            <div className="text-sm text-gray-600">Completion Rate</div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <MessageSquare className="text-purple-600" size={24} />
              <span className="text-xs text-gray-500">Score</span>
            </div>
            <div className="text-2xl font-bold">{groupAnalytics.memberSatisfaction.toFixed(1)}</div>
            <div className="text-sm text-gray-600">Satisfaction</div>
          </div>
        </div>
      )}

      {activeTab === 'jobs' && (
        <div className="space-y-6">
          {/* Job Completion Approvals Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Pending Approvals</h3>
            <JobCompletionApprovals groupId={id} />
          </div>

          {/* Posted Jobs Section */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Posted Jobs</h3>
              <button
                onClick={() => setShowJobPostingModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                Post New Job
              </button>
            </div>
            
            {/* Filter Bar */}
            <div className="flex gap-4 mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search jobs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="claimed">Claimed</option>
                <option value="pending_completion">Pending Completion</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            
            {/* Jobs List */}
            <div className="space-y-4">
              {filteredJobs.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Briefcase className="mx-auto text-gray-300 mb-3" size={48} />
                  <p className="text-gray-600">No jobs found</p>
                </div>
              ) : (
                filteredJobs.map(job => (
                  <div key={job.id} className="bg-white rounded-lg shadow p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-semibold">{job.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{job.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>Posted: {new Date(job.postedDate).toLocaleDateString()}</span>
                          <span>Reward: {job.tokenReward} tokens</span>
                          <span>Applications: {jobApplications[job.id]?.length || 0}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          job.status === 'open' ? 'bg-green-100 text-green-800' :
                          job.status === 'claimed' ? 'bg-blue-100 text-blue-800' :
                          job.status === 'pending_completion' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {job.status.replace('_', ' ')}
                        </span>
                        <button
                          onClick={() => handleDeleteJob(job.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <XCircle size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'members' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Group Members ({filteredMembers.length})</h3>
            <button
              onClick={() => handleExportData('members')}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <Download size={18} className="mr-1" />
              Export
            </button>
          </div>
          
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trust</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Units</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skills</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredMembers.map(member => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium">{member.name}</div>
                        <div className="text-sm text-gray-500">{member.email}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium">{member.trust}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(member.joined).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm">{member.units}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <span className="text-green-600">{member.skills.verified}</span> verified,{' '}
                        <span className="text-yellow-600">{member.skills.pending}</span> pending
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button className="text-gray-400 hover:text-gray-600">
                        <MoreVertical size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'communications' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Messages & Announcements</h3>
            <button
              onClick={() => setShowMessageModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              Send Announcement
            </button>
          </div>
          
          <div className="space-y-4">
            {(communications[id] || []).length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <MessageSquare className="mx-auto text-gray-300 mb-3" size={48} />
                <p className="text-gray-600">No messages sent yet</p>
              </div>
            ) : (
              (communications[id] || []).map(message => (
                <div key={message.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold">{message.subject}</h4>
                      <p className="text-sm text-gray-600 mt-1">{message.content}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>Sent: {new Date(message.sentAt).toLocaleString()}</span>
                        <span>To: {message.recipients}</span>
                        <span>Read by: {message.readBy.length} members</span>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      message.type === 'urgent' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {message.type}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Group Analytics</h3>
            <button
              onClick={() => handleExportData('analytics')}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <Download size={18} className="mr-1" />
              Export Report
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h4 className="font-semibold mb-4">Membership Metrics</h4>
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-gray-600">Total Members</dt>
                  <dd className="font-medium">{groupAnalytics.totalMembers}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-600">Active Members</dt>
                  <dd className="font-medium">{groupAnalytics.activeMembers}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-600">Average Trust Score</dt>
                  <dd className="font-medium">{groupAnalytics.averageTrust.toFixed(1)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-600">Growth Rate</dt>
                  <dd className="font-medium text-green-600">+{groupAnalytics.growthRate}%</dd>
                </div>
              </dl>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <h4 className="font-semibold mb-4">Job Performance</h4>
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-gray-600">Jobs Posted</dt>
                  <dd className="font-medium">{groupAnalytics.jobsPosted}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-600">Jobs Completed</dt>
                  <dd className="font-medium">{groupAnalytics.jobsCompleted}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-600">Completion Rate</dt>
                  <dd className="font-medium">{groupAnalytics.completionRate}%</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-600">Tokens Distributed</dt>
                  <dd className="font-medium">{groupAnalytics.tokensDistributed || 0}</dd>
                </div>
              </dl>
            </div>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="text-yellow-600 mr-2 flex-shrink-0" size={20} />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">Analytics Note</p>
                <p>These are demo analytics. In production, you'll see real-time data about your group's performance, member engagement, and financial metrics.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Job Posting Modal */}
      {showJobPostingModal && (
        <JobPostingForm
          isOpen={showJobPostingModal}
          onClose={() => setShowJobPostingModal(false)}
          onSubmit={handlePostJob}
          groupId={id}
          groupTitle={group.title}
        />
      )}

      {/* Message Modal */}
      <Modal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        title="Send Announcement"
        size="medium"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipients
            </label>
            <select
              value={messageForm.recipients}
              onChange={(e) => setMessageForm({ ...messageForm, recipients: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="all">All Members</option>
              <option value="active">Active Members Only</option>
              <option value="new">New Members (&lt; 7 days)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject
            </label>
            <input
              type="text"
              value={messageForm.subject}
              onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Announcement subject..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message
            </label>
            <textarea
              value={messageForm.content}
              onChange={(e) => setMessageForm({ ...messageForm, content: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Your message to members..."
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              onClick={() => setShowMessageModal(false)}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSendMessage}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center"
              disabled={!messageForm.subject || !messageForm.content}
            >
              <Send size={18} className="mr-2" />
              Send
            </button>
          </div>
        </div>
      </Modal>

      <BottomNav />
    </div>
  );
};

export default GroupAdminPage;